import React, { lazy } from 'react';
import {
  QueryProvider, client, bool, string, shape, extend, arrayOf, number, alias, params,
  useDataModel
} from '@thd-nucleus/data-sources';
import propTypes from 'prop-types';
import { useStoreId } from '@thd-nucleus/experience-context';

const WrappedFulfillment = lazy(() => import(
  /* webpackChunkName: "fulfillment" */
  '@thd-olt-component-react/fulfillment'
).then((mod) => {
  return {
    default: (props) => {
      const { FulfillmentPodShipping } = mod;
      return (
        <QueryProvider
          dataSource="catalog"
          cacheKey="selected-item-details-fulfillment"
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FulfillmentPodShipping {...props} />
        </QueryProvider>
      );
    }
  };
}));

const FulfillmentLazy = ({ itemId }) => {

  const storeId = useStoreId();
  const { data, loading, error } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    ssr: false
  });

  if (loading || error) return null;

  return (
    <WrappedFulfillment product={data?.product} />
  );
};

FulfillmentLazy.dataModel = extend({
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired(), dataSource: string() }).shape({
    itemId: string(),
    dataSources: string(),
    fulfillment: client(params({ storeId: string() }).shape({
      backordered: bool(),
      bossExcludedShipStates: string(),
      bodfsAssemblyEligible: bool(),
      inStoreAssemblyEligible: bool(),
      seasonStatusEligible: bool(),
      onlineStoreStatus: bool(),
      anchorStoreStatus: bool(),
      sthExcludedShipState: string(),
      bossExcludedShipState: string(),
      fulfillmentOptions: shape({
        type: string(),
        fulfillable: bool(),
        services: arrayOf(shape({
          type: string(),
          isBossDominant: bool(),
          mode: shape({
            code: string(),
            desc: string(),
            longDesc: string(),
            group: string()
          }),
          isDefault: bool(),
          deliveryCharge: string(),
          freeDeliveryThreshold: number(),
          hasFreeShipping: bool(),
          itemLevelFreeShipping: bool(),
          deliveryTimeline: string(),
          deliveryDates: shape({
            startDate: string(),
            endDate: string(),
            timeOfArrival: string()
          }),
          totalCharge: number(),
          pickupTime: string(),
          locations: arrayOf(shape({
            type: string(),
            locationId: string(),
            isAnchor: bool(),
            storeName: string(),
            storePhone: string(),
            state: string(),
            distance: number(),
            inventory: shape({
              quantity: number(),
              isInStock: bool(),
              isOutOfStock: bool(),
              isLimitedQuantity: bool(),
              isUnavailable: bool(),
              maxAllowedBopisQty: number(),
              minAllowedBopisQty: number(),
            }),
            curbsidePickupFlag: bool(),
            isBuyInStoreCheckNearBy: bool(),
          })),
        }))
      }),
    })),
    identifiers: shape({
      itemId: string(),
    })
  })
});

FulfillmentLazy.displayName = 'FulfillmentLazy';

FulfillmentLazy.propTypes = {
  itemId: propTypes.string
};

FulfillmentLazy.defaultProps = {
  itemId: '',
};

export { FulfillmentLazy };
